import React, { useEffect, useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { companyLinks, productGroups } from '../data/navigation';
import { easeOutStrong } from '../utils/motion';
type Dropdown = 'products' | 'company' | null;
const dropdownMotion = {
  initial: {
    opacity: 0,
    y: -6
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -6
  },
  transition: {
    duration: 0.18,
    ease: easeOutStrong
  }
};
const topLinkClass = 'text-sm font-medium text-ink-muted hover:text-navy transition-colors duration-150';
const mobileLinkClass = 'block px-3 py-2 text-base font-medium text-ink-dark hover:bg-surface-muted rounded-md';
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<Dropdown>(null);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const closeMobile = () => setMobileMenuOpen(false);
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-[padding,background-color,box-shadow] duration-200 ${isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-line py-3' : 'bg-white border-b border-line py-4'}`} data-id="element-778" data-mp="mgnm2lxellz8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-779" data-mp="webg19n6qpgc">
        <div className="flex justify-between items-center" data-id="element-780" data-mp="10f3ow1s5asc3">
          <a href="/" className="flex items-center" aria-label="SyNeural home" data-id="element-781" data-mp="156vh0gqy964c">
            <img src="https://cdn.magicpatterns.com/uploads/vBy52qowPNFNY53vkUADE7/SyNeural-logo-no-tagline-half-size.svg" alt="SyNeural" className={`w-auto transition-[height] duration-200 ease-out ${isScrolled ? 'h-8' : 'h-10'}`} data-id="element-782" data-mp="158ygdrdzsblq" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Main" data-id="element-783" data-mp="ufqtzoyttoq3">
            <ul className="flex items-center gap-7" data-id="element-784" data-mp="udnzarmqb7f1">
              {/* Products */}
              <li className="relative" onMouseEnter={() => setOpenDropdown('products')} onMouseLeave={() => setOpenDropdown(null)} data-id="element-785" data-mp="pet4ssfefuf3">
                <button type="button" onClick={() => setOpenDropdown(openDropdown === 'products' ? null : 'products')} className={`${topLinkClass} flex items-center gap-1 py-2`} aria-expanded={openDropdown === 'products'} data-id="element-786" data-mp="zd0gowutzma8">
                  Products
                  <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-150 ${openDropdown === 'products' ? 'rotate-180' : ''}`} data-id="element-787" data-mp="1i51pos1bksxd" />
                </button>
                <AnimatePresence data-id="element-788" data-mp="3jz11deictcal">
                  {openDropdown === 'products' && <motion.div {...dropdownMotion} className="absolute top-full left-0 pt-2 w-[26rem]" data-id="element-789" data-mp="1zfp1p1x2q7js">
                      <div className="bg-white rounded-lg shadow-lg border border-line py-2" data-id="element-790" data-mp="1po2hwh6o705f">
                        {productGroups.map((group, groupIndex) => <div key={group.heading} className={groupIndex > 0 ? 'border-t border-line mt-2 pt-2' : ''} data-id="element-791" data-mp="1gontdna2cznk" data-mpi={groupIndex}>
                            <p className="px-4 pt-2 pb-1 text-xs font-semibold text-teal" data-id="element-792" data-mp="1kq9dntoxgg7s">{group.heading}</p>
                            {group.items.map(item => <div key={item.href} data-id="element-793" data-mp="1bpbateq86eqs">
                                <a href={item.href} onClick={() => setOpenDropdown(null)} className="block px-4 py-2 text-sm text-ink-dark hover:bg-surface-muted transition-colors duration-150" data-id="element-794" data-mp="1bq4bbxylc80h">
                                  <span className="font-semibold" data-id="element-795" data-mp="15yqhpyt30ouv">{item.label}</span>
                                  {item.badge && <span className="text-ink-muted font-normal" data-id="element-796" data-mp="wzmxgri6i3es"> · {item.badge}</span>}
                                  <span className="block text-xs text-ink-muted mt-0.5" data-id="element-797" data-mp="s0tlx4mgcmh9">{item.description}</span>
                                </a>
                                {item.children && <div className="ml-6 mb-1 border-l border-line" data-id="element-798" data-mp="ia789fyaq617">
                                    {item.children.map(child => <a key={child.href} href={child.href} onClick={() => setOpenDropdown(null)} className="block pl-4 pr-4 py-1.5 text-sm hover:bg-surface-muted transition-colors duration-150" data-id="element-799" data-mp="9bderyg9hvrv">
                                        <span className="font-medium text-ink-dark" data-id="element-800" data-mp="2fy1bhuadmaq3">{child.label}</span>
                                        <span className="block text-xs text-ink-muted" data-id="element-801" data-mp="2kz8llzzzrlzx">{child.description}</span>
                                      </a>)}
                                  </div>}
                              </div>)}
                          </div>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>

              <li data-id="element-802" data-mp="262hibfn75q51">
                <a href="/solutions" className={topLinkClass} data-id="element-803" data-mp="2630zgu50wai0">
                  Solutions
                </a>
              </li>

              {/* Company */}
              <li className="relative" onMouseEnter={() => setOpenDropdown('company')} onMouseLeave={() => setOpenDropdown(null)} data-id="element-804" data-mp="1djkb4nqa0o3j">
                <button type="button" onClick={() => setOpenDropdown(openDropdown === 'company' ? null : 'company')} className={`${topLinkClass} flex items-center gap-1 py-2`} aria-expanded={openDropdown === 'company'} data-id="element-805" data-mp="2bvervwdqk25k">
                  Company
                  <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-150 ${openDropdown === 'company' ? 'rotate-180' : ''}`} data-id="element-806" data-mp="1xwx829qyb9sx" />
                </button>
                <AnimatePresence data-id="element-807" data-mp="22uoz71jrurw2">
                  {openDropdown === 'company' && <motion.div {...dropdownMotion} className="absolute top-full left-0 pt-2 w-72" data-id="element-808" data-mp="3ibz3prki8tde">
                      <div className="bg-white rounded-lg shadow-lg border border-line py-2" data-id="element-809" data-mp="3ebopjrmk6wng">
                        {companyLinks.map(link => <a key={link.href} href={link.href} onClick={() => setOpenDropdown(null)} className="block px-4 py-2 text-sm text-ink-dark hover:bg-surface-muted transition-colors duration-150" data-id="element-810" data-mp="3lwiu9cp7eibf">
                            <span className="block font-semibold" data-id="element-811" data-mp="8f03vljdyz7v">{link.label}</span>
                            <span className="block text-xs text-ink-muted" data-id="element-812" data-mp="decxcp4rj4jf">{link.description}</span>
                          </a>)}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>

              <li data-id="element-813" data-mp="redze7wrki0h">
                <a href="/contact" className={topLinkClass} data-id="element-814" data-mp="rdvukmw1cvgy">
                  Contact
                </a>
              </li>
            </ul>
            <div className="w-px h-5 bg-line" aria-hidden="true" data-id="element-815" data-mp="w6owz4417rpv"></div>
            <a href="/contact" className="bg-navy hover:bg-navy-light text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors duration-150 shadow-sm whitespace-nowrap" data-id="element-816" data-mp="2s8ij2fi1ss6o">
              Request a Demo
            </a>
          </nav>

          <button type="button" className="lg:hidden text-ink-dark p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" aria-expanded={mobileMenuOpen} data-id="element-817" data-mp="2ehds01sqx87y">
            {mobileMenuOpen ? <X size={24} data-id="element-818" data-mp="373otrfs39f6c" /> : <Menu size={24} data-id="element-819" data-mp="3266gjd71gdsj" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Drawer */}
      <AnimatePresence data-id="element-820" data-mp="2x7v733kno5au">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.22,
        ease: easeOutStrong
      }} className="lg:hidden bg-white border-b border-line overflow-hidden" data-id="element-821" data-mp="2x73oqug2k42i">
            <nav className="px-4 pt-2 pb-6 space-y-1 max-h-[80vh] overflow-y-auto" aria-label="Mobile" data-id="element-822" data-mp="1gncjtyx83hi7">
              <div className="border-b border-line pb-2 mb-2" data-id="element-823" data-mp="1lmpgfbdwc2xz">
                <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider px-3 py-2" data-id="element-824" data-mp="1ll4mnnzeoo16">Products</p>
                {productGroups.map(group => <div key={group.heading} className="mb-1" data-id="element-825" data-mp="1udpq7wuguo5f">
                    <p className="px-3 pt-2 pb-1 text-xs font-semibold text-teal" data-id="element-826" data-mp="1zdu6pmvdjivn">{group.heading}</p>
                    {group.items.map(item => <div key={item.href} data-id="element-827" data-mp="24e8h3bkg74wu">
                        <a href={item.href} className={mobileLinkClass} onClick={closeMobile} data-id="element-828" data-mp="29c07u29cwkzg">
                          {item.label}
                          {item.badge && <span className="text-sm font-normal text-ink-muted" data-id="element-829" data-mp="a07j0swq2lxt"> · {item.badge}</span>}
                        </a>
                        {item.children && <div className="ml-4 border-l border-line" data-id="element-830" data-mp="2txo6dkrf30nf">
                            {item.children.map(child => <a key={child.href} href={child.href} className="block pl-4 pr-3 py-2 text-base text-ink-dark hover:bg-surface-muted rounded-md" onClick={closeMobile} data-id="element-831" data-mp="2ywixrb3g9xj5">
                                {child.label}
                              </a>)}
                          </div>}
                      </div>)}
                  </div>)}
              </div>

              <a href="/solutions" className={`${mobileLinkClass} py-3`} onClick={closeMobile} data-id="element-832" data-mp="3qp9lsyazcfxb">
                Solutions
              </a>

              <div className="border-y border-line py-2 my-2" data-id="element-833" data-mp="4ffh2jnbozu5">
                <p className="text-xs font-semibold text-ink-muted uppercase tracking-wider px-3 py-2" data-id="element-834" data-mp="4duh2ei8ooow">Company</p>
                {companyLinks.map(link => <a key={link.href} href={link.href} className={mobileLinkClass} onClick={closeMobile} data-id="element-835" data-mp="9k1433xgvha9">
                    {link.label}
                  </a>)}
              </div>

              <a href="/contact" className={`${mobileLinkClass} py-3`} onClick={closeMobile} data-id="element-836" data-mp="lfo9gjdqgd6">
                Contact
              </a>
              <div className="pt-4 px-3" data-id="element-837" data-mp="xchovm8l9nd9">
                <a href="/contact" onClick={closeMobile} className="block w-full text-center bg-navy text-white font-medium px-5 py-3 rounded-md shadow-sm" data-id="element-838" data-mp="sb0l9iypzmsz">
                  Request a Demo
                </a>
              </div>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
}